import {
  ButtonProps,
  InputLabelProps,
  OutlinedInputProps,
  SelectProps,
  TextFieldProps,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { getCarbonThemeOptions } from "common/theme/theme";
import { LanguageMap } from "common/i18nConstants";
import { MUI_DATA_GRID_LANGUAGES } from "portal/i18nMui";
import { TFunction } from "i18next";
import { Theme } from "@mui/system";
import { twMerge } from "tailwind-merge";
import clsx, { ClassValue } from "clsx";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "portal/../tailwind.config.js";
import type {} from "@mui/x-data-grid-premium/themeAugmentation";

export const { theme } = resolveConfig(tailwindConfig);

export const classes = (...inputs: ClassValue[]): string =>
  twMerge(clsx(inputs));

/** @deprecated */
export const STATUS_TEXT = {
  GRAY: "text-gray-500",
  RED: "text-red-500",
  ORANGE: "text-orange-200",
  YELLOW: "text-yellow-500",
  GREEN: "text-green-500",
};
/** @deprecated */
export const STATUS_BG = {
  RED: "bg-red-500 text-white",
  YELLOW: "bg-yellow-500 text-white",
  GREEN: "bg-green-500 text-white",
};

/** @deprecated */
const INPUT_LABEL_CLASS = "[&.Mui-focused]:text-white";

/** @deprecated */
export const OUTLINED_INPUT_OUTLINE = classes(
  "border-darken-300",
  "group-[.Mui-focused]:border-white group-hover:border-lighten-500",
  "group-[.Mui-readOnly]:border-0 group-[.Mui-readOnly]:cursor-default"
);

/** @deprecated */
const OUTLINED_INPUT_DARK_OUTLINE = classes(
  "border-lighten-300",
  "group-[.Mui-focused]:border-white group-hover:border-lighten-500",
  "group-[.Mui-readOnly]:border-0 group-[.Mui-readOnly]:cursor-default"
);

/** @deprecated */
export const LABEL: Pick<InputLabelProps, "className"> = {
  className: INPUT_LABEL_CLASS,
};

/** @deprecated */
export const TEXT_FIELD: Pick<TextFieldProps, "variant" | "slotProps"> = {
  variant: "outlined",
  slotProps: {
    inputLabel: { className: INPUT_LABEL_CLASS },
    input: {
      className: "group",
      slotProps: {
        notchedOutline: { className: OUTLINED_INPUT_OUTLINE },
      },
    },
  },
};

/** @deprecated */
export const TEXT_FIELD_DARK: Pick<TextFieldProps, "variant" | "slotProps"> = {
  variant: "outlined",
  slotProps: {
    inputLabel: { className: INPUT_LABEL_CLASS },
    input: {
      className: "group",
      slotProps: {
        notchedOutline: { className: OUTLINED_INPUT_DARK_OUTLINE },
      },
    },
  },
};

/** @deprecated */
export const INPUT: Pick<OutlinedInputProps, "className" | "slotProps"> = {
  className: "group",
  slotProps: {
    input: { className: "group-[.Mui-readOnly]:cursor-default" },
    notchedOutline: { className: OUTLINED_INPUT_OUTLINE },
  },
};

/** @deprecated */
export const INPUT_DARK: Pick<OutlinedInputProps, "className" | "slotProps"> = {
  className: "group",
  slotProps: {
    input: { className: "group-[.Mui-readOnly]:cursor-default" },
    notchedOutline: { className: OUTLINED_INPUT_DARK_OUTLINE },
  },
};

/** @deprecated */
export const SELECT: Pick<SelectProps, "className"> = {
  className:
    "w-80 max-w-full [&_.MuiSelect-icon]:text-darken-300 hover:[&_.MuiSelect-icon]:text-white group-[.Mui-readOnly]:[&_.MuiSelect-icon]:hidden [&_.MuiSelect-iconOpen]:text-white",
};

/** @deprecated */
export const SELECT_DARK: Pick<SelectProps, "className"> = {
  className: classes(
    "w-80 max-w-full text-white",
    "[&_.MuiSelect-icon]:text-lighten-300 hover:[&_.MuiSelect-icon]:text-white",
    "[&_.MuiOutlinedInput-notchedOutline]:border-lighten-300",
    "hover:[&_.MuiOutlinedInput-notchedOutline]:border-lighten-300",
    "[&.Mui-focused_.MuiOutlinedInput-notchedOutline]:border-lighten-300"
  ),
};

/** @deprecated */
const BUTTON_BASE_CLASS = classes(
  "h-10 whitespace-nowrap",
  "disabled:bg-zinc-500 disabled:text-lighten-500"
);

/** @deprecated */
export const BUTTON: Pick<ButtonProps, "variant" | "className"> = {
  variant: "contained",
  className: BUTTON_BASE_CLASS,
};

/** @deprecated */
export const SMALL_BUTTON: Pick<ButtonProps, "variant" | "className" | "size"> =
  {
    ...BUTTON,
    className: classes(BUTTON_BASE_CLASS, "h-8"),
    size: "small",
  };

/** @deprecated */
export const LOADING_BUTTON: Pick<
  ButtonProps,
  "variant" | "className" | "loadingPosition" | "classes"
> = {
  ...BUTTON,
  loadingPosition: "start",
  classes: {
    loadingIndicator: "text-white",
  },
};

/** @deprecated */
export const WHITE_BUTTON: Pick<ButtonProps, "variant" | "className"> = {
  ...BUTTON,
  className: classes(
    BUTTON_BASE_CLASS,
    "bg-white text-black",
    "hover:bg-zinc-100 active:bg-zinc-200"
  ),
};

/** @deprecated */
export const RED_BUTTON: Pick<ButtonProps, "variant" | "className"> = {
  ...BUTTON,
  className: classes(
    BUTTON_BASE_CLASS,
    "bg-red-700 text-white",
    "hover:bg-red-600 active:bg-zinc-500"
  ),
};

/** @deprecated */
export const YELLOW_BUTTON: Pick<ButtonProps, "variant" | "className"> = {
  ...BUTTON,
  className: classes(
    BUTTON_BASE_CLASS,
    "bg-yellow-700 text-black",
    "hover:bg-yellow-600 active:bg-yellow-500"
  ),
};

/** @deprecated */
export const GREEN_BUTTON: Pick<ButtonProps, "variant" | "className"> = {
  ...BUTTON,
  className: classes(
    BUTTON_BASE_CLASS,
    "bg-green-700 text-white",
    "hover:bg-green-600 active:bg-zinc-500"
  ),
};

/** @deprecated */
export const SMALL_WHITE_BUTTON: Pick<ButtonProps, "variant" | "className"> = {
  ...SMALL_BUTTON,
  className: classes(
    SMALL_BUTTON.className,
    "bg-white text-black",
    "hover:bg-zinc-100 active:bg-zinc-200"
  ),
};

/** @deprecated */
export const SMALL_SECRET_RAINBOW_BUTTON: Pick<
  ButtonProps,
  "variant" | "className" | "size"
> = {
  ...SMALL_BUTTON,
  variant: "outlined",
  className: classes(
    SMALL_BUTTON.className,
    "group border-white text-white",
    "hover:bg-darken-200"
  ),
};

/** @deprecated */
export const BLUE_BUTTON: Pick<ButtonProps, "variant" | "className"> = {
  ...BUTTON,
  className: classes(
    BUTTON_BASE_CLASS,
    "bg-blue-500 text-white",
    "hover:bg-cyan-600 active:bg-cyan-500"
  ),
};

/** @deprecated */
export const BLUE_LOADING_BUTTON = {
  ...LOADING_BUTTON,
  ...BLUE_BUTTON,
};

/** @deprecated */
export const RED_LOADING_BUTTON = {
  ...LOADING_BUTTON,
  ...RED_BUTTON,
};

/** @deprecated */
export const YELLOW_LOADING_BUTTON = {
  ...LOADING_BUTTON,
  ...YELLOW_BUTTON,
};

/** @deprecated */
export const SMALL_LABEL: Pick<InputLabelProps, "className" | "size"> = {
  ...LABEL,
  size: "small",
};

/** @deprecated */
export const SMALL_TEXT_FIELD: Pick<
  TextFieldProps,
  "size" | "variant" | "slotProps"
> = {
  ...TEXT_FIELD,
  size: "small",
};

/** @deprecated */
export const SMALL_TEXT_FIELD_DARK: Pick<
  TextFieldProps,
  "size" | "variant" | "slotProps"
> = {
  ...TEXT_FIELD_DARK,
  size: "small",
};

/** @deprecated */
export const SMALL_INPUT_DARK: Pick<
  OutlinedInputProps,
  "size" | "className" | "slotProps"
> = {
  ...INPUT_DARK,
  size: "small",
};

/** @deprecated */
export const SMALL_SELECT_DARK: Pick<SelectProps, "size" | "className"> = {
  ...SELECT_DARK,
  size: "small",
};

export interface ThemeProps {
  field: Partial<TextFieldProps>;
  label: Partial<InputLabelProps>;
  input: Partial<OutlinedInputProps>;
  select: Partial<SelectProps>;
}

/** @deprecated */
export const BG_BACKDROP = "bg-carbon-gray-dark";
/** @deprecated */
export const BG_CARD = "bg-carbon-gray-medium";

export const getMuiTheme = (
  mode: "light" | "dark" = "dark",
  language: keyof LanguageMap<any>,
  t: TFunction
): Theme =>
  createTheme(
    getCarbonThemeOptions(mode),
    ...MUI_DATA_GRID_LANGUAGES[language](t)
  );

export {
  type Breakpoints,
  CHIP_COLORS,
  carbon,
  GRAPH_COLORS,
  FLAG_DISABLE_SSR_WARNING,
  STALE_CHIP_COLOR,
} from "common/theme/theme";
